import '../scss/funzi.scss';
// Framework routing utilities: 
// - registerRoute: Registers a new route and its handler
// - startRouter: Initializes the router and handles navigation events
// - navigateTo: Programmatically navigates to a given route
// - getRouteByPath: Retrieves route configuration by path
import { registerRoute, startRouter, navigateTo, getRouteByPath } from './framework/index.ts';
import About from './pages/About.ts';
import DashboardPage from './pages/dashboard/Index.ts';
import ProfilePage from './pages/dashboard/Profile.ts';
import PVPage from "./pages/dashboard/PV.ts";
import BessPage from './pages/dashboard/Bess.ts';
import GeneratorPage from './pages/dashboard/Generator.ts';
import MetStationPage from './pages/dashboard/MetStation.ts';
import GridPage from './pages/dashboard/Grid.ts';
import CarbonCreditsPage from './pages/dashboard/carbon-credits.ts';
import LoginPage from './pages/Login.ts';
import RegisterPage from './pages/Register.ts';
import OverviewPage from './pages/Overview.ts';
import CommunicationPage from './pages/dashboard/Communication.ts';
import DataTablePage from './pages/dashboard/DataTable.ts';
import HistoricalPage from './pages/dashboard/Historical.ts';
import AlarmPage from './pages/dashboard/Alarms.ts';
// import SLDDesignPage from './pages/dashboard/SLDDesign.ts';
// import SLDViewPage from './pages/dashboard/SLDView.ts';
import SchematicsPage from './pages/dashboard/Schematics.ts';
// import HeatmapPage from './pages/dashboard/Heatmap.ts';
// import PowerControlPage from './pages/dashboard/PowerControl.ts';
import DocumentsPage from './pages/dashboard/Documents.ts';
import EventViewerPage from './pages/dashboard/EventViewer.ts';
import UserManagementPage from './pages/dashboard/UserManagement.ts';
import ReportsPage from './pages/dashboard/Reports.ts';
import MainNavbar from './components/MainNavbar.ts';
import DashboardNavbar from './components/DashboardNavBar.ts';
import DashboardSidebar from './components/DashboardSidebar.ts';
import { authContext } from './context/AuthContext.ts';
import { initMqtt, restoreMqtt } from './services/mqttClient.ts';
import LicenseExpiredPage from './pages/LicenseExpired.ts';
import { AlarmEngine } from "./services/AlarmEngine.ts";
import { globalState } from "./framework/globalState.ts";
import { usercycle, fetchData, getRefreshToken, setAccessToken, getAccessToken } from "./services/api.ts";
import { APIModel } from "./services/apiModel.ts";


const mount = document.getElementById('app');
const navbarMount = document.getElementById('navbar');
const appContainer = document.getElementById('app-container') || mount;
const sidebarMount = document.querySelector('.sidebar');
const splash = document.getElementById('splash');
const initialPathOnLoad = `${window.location.pathname}${window.location.search || ''}`;

function isAuthPublicPath(path: string): boolean {
  return ['/login', '/register', '/'].includes(path);
}

function hydrateAuthStateFromStorage(): void {
  const token = localStorage.getItem('token');
  const userRaw = localStorage.getItem('user');
  if (!token || !userRaw) return;

  try {
    const user = JSON.parse(userRaw);
    if (!user) return;
    globalState.user = user;
    setAccessToken(token);
  } catch {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }
}

// ----------------- Routes -----------------
registerRoute('/about', About);
registerRoute('/login', LoginPage);
registerRoute('/register', RegisterPage);
registerRoute('/overview', OverviewPage, { auth: true });
registerRoute('/dashboard', DashboardPage, { auth: true });
registerRoute('/dashboard/profile', ProfilePage, { auth: true });
registerRoute('/dashboard/pv', PVPage, { auth: true });
registerRoute('/dashboard/bess', BessPage, { auth: true });
registerRoute('/dashboard/generator', GeneratorPage, { auth: true });
registerRoute('/dashboard/met-station', MetStationPage, { auth: true });
registerRoute('/dashboard/grid', GridPage, { auth: true });
registerRoute('/dashboard/carbon-credit', CarbonCreditsPage, { auth: true });
registerRoute('/dashboard/communication', CommunicationPage, { auth: true });
registerRoute('/dashboard/data-table', DataTablePage, { auth: true });
registerRoute('/dashboard/alarms', AlarmPage, { auth: true });
registerRoute('/dashboard/historical', HistoricalPage, { auth: true });
// registerRoute('/dashboard/sld-design', SLDDesignPage, { auth: true });
// registerRoute('/dashboard/sld-view', SLDViewPage, { auth: true });
registerRoute('/dashboard/schematics', SchematicsPage, { auth: true });
// registerRoute('/dashboard/heatmap', HeatmapPage, { auth: true });
// registerRoute('/dashboard/power-control', PowerControlPage, { auth: true });
registerRoute('/dashboard/documents', DocumentsPage, { auth: true });
registerRoute('/dashboard/event-viewer', EventViewerPage, { auth: true });
registerRoute('/dashboard/user-management', UserManagementPage, { auth: true });
registerRoute('/dashboard/reports', ReportsPage, { auth: true });
registerRoute('/license-expired', LicenseExpiredPage, { auth: true });
registerRoute('*', () => {
  const el = document.createElement('div');
  el.textContent = 'Page not found';
  return el;
});

// ----------------- Navbar Renderer -----------------
function renderNavbar(path) {
  navbarMount.innerHTML = '';
  if (
    path.startsWith('/login') ||
    path.startsWith('/register') ||
    path.startsWith('/about') ||
    path.startsWith('/overview')
  ) {
    navbarMount.style.display = 'block';
    navbarMount.appendChild(MainNavbar());
    const userDropdownBtn = document.querySelector('#userDropdown');
    const dropdownMenu = document.querySelector('.dropdown-menu');
  } else if (path.startsWith('/dashboard')) {
    navbarMount.appendChild(DashboardNavbar());
  }
}

// ----------------- Sidebar Renderer -----------------
function renderSidebar(path) {
  const sidebarMount = document.querySelector('.sidebar');
  if (!sidebarMount) return;
  if (path.startsWith('/dashboard')) {
    DashboardSidebar();
  } else {
    sidebarMount.innerHTML = '';
    sidebarMount.removeAttribute('data-initialized');
  }
}

// ----------------- Router -----------------
hydrateAuthStateFromStorage();

startRouter(mount, {
  mode: 'history',
  onRouteChange: (path) => {
     const route = getRouteByPath(path);
    if (route?.auth && !authContext.isLoggedIn) {
      // User not logged in but route needs auth
      navigateTo('/login');
      return;
    }
    renderNavbar(path);
    renderSidebar(path);
  },
});

// ----------------- Initial App Init -----------------
async function initApp() {
  const refreshToken = getRefreshToken();
  let user;

  if (refreshToken) {
    try {
      // fetchData already handles 401 → refresh → retry internally with race protection.
      // Do NOT call the refresh endpoint manually here — that races with any concurrent
      // fetchData calls made by the page that just rendered, causing token-rotation failures.
      const apiModel = new APIModel();
      const API = apiModel.getEndpoints();

      // 1️⃣ Fetch user — fetchData will auto-refresh the access token if it's expired
      user = await fetchData(API.auth.me);
      const accessToken = getAccessToken()!;
      authContext.login(user, accessToken);

      // 2️⃣ Initialize MQTT in background
      initMqtt(accessToken).catch((err) => {
        console.warn("⚠️ MQTT connection failed on app load, will retry in background:", err.message);
      });

      const path = window.location.pathname;
      if (isAuthPublicPath(path)) {
        const initialBase = (initialPathOnLoad.split('?')[0] || '/');
        const target = !isAuthPublicPath(initialBase) ? initialPathOnLoad : '/overview';
        navigateTo(target, { replace: true });
      }
    } catch (err) {
      console.error('Session restore failed:', err);
      // Clear invalid tokens and redirect to login
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("token");
      globalState.token = null;
      authContext.logout();
      if (window.location.pathname !== '/register') {
        navigateTo('/login', { replace: true });
      }
    }
  } else {
    if (!['/login', '/register'].includes(window.location.pathname)) {
      navigateTo('/login', { replace: true });
    }
  }

  // SPLASH SCREEN REMOVAL — ONLY ONCE, ALWAYS
  if (splash) {
    console.log("splash");
    splash.style.transition = 'opacity 0.5s ease';
    splash.style.opacity = '0';
    setTimeout(() => {
      splash.remove(); // Permanently gone
    }, 600);
  }
}

// RUN ONLY ONCE
initApp().then(() => {
  restoreMqtt(); // Reconnect MQTT if needed (also non-blocking)
});

// ----------------- Initial Render (safe to call anytime) -----------------
renderNavbar(window.location.pathname);
renderSidebar(window.location.pathname);

// ----------------- Navigation -----------------
document.addEventListener('click', (e) => {
  const a = e.target.closest('[data-link]');
  if (!a) return;
  e.preventDefault();
  const path = a.dataset.link || a.getAttribute('href');
  navigateTo(path);
  renderNavbar(path);
  renderSidebar(path);
});

document.addEventListener('DOMContentLoaded', () => {
  if (localStorage.getItem('theme') === 'dark') {
    document.body.classList.add('dark-mode');
  }
  if (globalState.currentSite && globalState.registers?.length) {
    AlarmEngine.instance.init();
  }

  // Re-init if site changes
  globalState.on("currentSite", async (site) => {
    AlarmEngine.instance.cleanup();
    AlarmEngine.instance.init();
  });
});

document.addEventListener('notification', (e) => {
  const { message, type } = e.detail;
  const banner = document.createElement('div');
  banner.className = `ws-notification ${type}`;
  banner.textContent = message;
  document.body.appendChild(banner);
  setTimeout(() => banner.remove(), 5000);
});
